import React, { Fragment, useEffect, useRef, useState } from 'react'
import useOutsideClick from './useOutsideClick'
import useAnnounceResults from '../hooks/useAnnounceResults'
import useDebouncedSearch from './useDebouncedSearch'

import { global } from './constants'
import SearchResults from './SearchResults'

import './style.scss'
import { turboRequest } from '../../helpers/turbo_helper'
import { SearchModal } from './SearchModal'
import useAlgoliaSearch from './useAlgoliaSearch'
import { fetchResponse } from '../../controllers/shared/utils/fetch_data'

const Typeahead = ({
  unique_id,
  input_id,
  input_name,
  input_label,
  hidden_input_name,
  hidden_input_value,
  select_new_client_path,
  select_new_client_modal_selector,
  select_menu_item_url,
  readonly,
  disabled,
  error_message,
  placeholder,
  start_selected,
  floating_search,
  anonymous_link,
  hide_add_new_item_button,
  initial_suggestion_path,
  suggestion_only_on_focus = false,
  client_only = false
}) => {
  const [selectedId, setSelectedId] = useState(hidden_input_value)
  const [modalOpened, setModalOpened] = useState(false)
  const [selectedName, setSelectedName] = useState(null)
  const [suggestions, setSuggestions] = useState(null)
  const [loading, setLoading] = useState(!!initial_suggestion_path)
  const [isFocused, setIsFocused] = useState(false)

  const ref = useRef()
  const valueRef = useRef()
  const inputRef = useRef()
  const inputHiddenRef = useRef()
  const turboFrameRef = useRef()
  const isInitialMount = useRef(true)

  const { client } = useAlgoliaSearch()

  const announce = useAnnounceResults()

  const searchFunction = async inputText => {
    const searchParams = {
      query: inputText,
      ...global.algoliaSettings
    }
    
    if (client_only) {
      searchParams.filters = 'is_client:true'
    }
    
    const response = await client.searchSingleIndex(
      {
        indexName: global.indexName,
        searchParams: searchParams
      },
      {
        headers: { 'X-Algolia-UserToken': global.userId }
      }
    )
    const { hits, queryID } = response

    announce(hits.length)
    return hits.map(hit => {
      return { ...hit, queryId: queryID }
    })
  }

  const onSelectIdChange = () => {
    if (isInitialMount.current) {
      // We only want to run this on update, not when the component mounts
      isInitialMount.current = false
    } else {
      fetchPersonDisplayInfo(selectedId)

      valueRef.current.dispatchEvent(
        new CustomEvent('autocomplete.change', { bubbles: true, detail: { id: unique_id, value: selectedId } })
      )
      valueRef.current.dispatchEvent(
        new CustomEvent('typeahead:changed', { bubbles: true, detail: { id: unique_id, value: selectedId } })
      )
    }
  }

  const onInit = () => {
    if (selectedId) {
      fetchPersonDisplayInfo(selectedId)
    }

    if (start_selected) {
      inputRef.current.focus()
    }

    if (initial_suggestion_path) {
      fetchSuggestionInfo().then(() => {
        setLoading(false)
      })
    }
  }

  const { inputText, setInputText, searchResults } = useDebouncedSearch(searchFunction)

  useEffect(onSelectIdChange, [selectedId])
  useEffect(onInit, [])

  const modalIsOpened = floating_search && modalOpened
  const showPlaceholder =
    !selectedId && modalIsOpened && !inputText && (!searchResults.result || searchResults.result.length === 0)

  useOutsideClick(ref, () => {
    if (modalIsOpened || !floating_search) setInputText('')
  })

  const fetchPersonDisplayInfo = personId => {
    if (!personId) {
      return
    }

    closeModal()

    const url = new URL(select_menu_item_url)
    let params = new URLSearchParams(url.search)
    params.append('item_id', personId)

    if (unique_id) {
      params.append('unique_id', unique_id)
    }

    turboRequest(`${url.pathname}?${params}`)
  }

  const fetchSuggestionInfo = async () => {
    const response = await fetchResponse(initial_suggestion_path)

    setSuggestions(await response.json())
  }

  const onPersonSelected = (_event, _role, _position, person) => {
    setSelectedId(person.id)
    setSelectedName(person.full_name)
  }

  const onSearchFieldChange = event => {
    setSelectedId(null)
    setSelectedName(null)
    setInputText(event.target.value)
  }

  const clearCurrentSelected = () => {
    setSelectedId(null)
    setSelectedName(null)
    setInputText('')
    turboFrameRef.current.innerHTML = ''
    // Delay to allow other events to be processed
    setTimeout(() => (valueRef.current.value = null), 0)
  }

  const onNewClientSelected = () => {
    if (select_new_client_path) {
      let urlParams = new URLSearchParams(select_new_client_path)
      urlParams.append('full_name', inputText)

      let url = ''
      if (select_new_client_path.includes('?')) {
        url = `${select_new_client_path}&${urlParams}`
      } else {
        url = `${select_new_client_path}?${urlParams}`
      }

      turboRequest(url)
    } else if (select_new_client_modal_selector) {
      const targetModal = document.querySelector(select_new_client_modal_selector)
      if (targetModal == null) {
        throw new Error(`Could not find target modal with selector '${select_new_client_modal_selector}'!`)
      }
      targetModal.dispatchEvent(new CustomEvent('modal:open'))
    } else {
      inputHiddenRef.current.value = inputText
    }

    closeModal(!!select_new_client_path)
  }

  const openModal = () => {
    setModalOpened(true)
    setIsFocused(true)
    setTimeout(() => inputRef.current.focus(), 0)
  }

  const closeModal = (clearText = true) => {
    if (!floating_search) {
      return
    }

    setModalOpened(false)
    setIsFocused(false)
    setTimeout(() => {
      if (clearText) setInputText('')
      inputRef.current.blur()
    }, 0)
  }

  const handleInputFocus = () => {
    setIsFocused(true)
    openModal()
  }

  const handleInputBlur = () => {
    setTimeout(() => setIsFocused(false), 200)
  }

  return (
    <Fragment>
      <SearchModal open={modalIsOpened} onBackdropClicked={closeModal} id={input_id}>
        <div ref={ref} className={modalIsOpened ? 'search-container' : undefined}>
          <div hidden={!selectedId} className={'typeahead-container'}>
            <div className={'selected-menu-item-container'}>
              <div className={'autocomplete-selected-item autocomplete-grid-row'}>
                <turbo-frame
                  id={unique_id ? `menu_item_selection_${unique_id}` : 'menu_item_selection'}
                  ref={turboFrameRef}
                  className={'typeahead-turbo-frame'}
                ></turbo-frame>
              </div>
              <button
                className={'btn-close selected-item-close-button d-flex align-items-center'}
                hidden={!selectedId || readonly}
                style={{ visibility: readonly ? 'hidden' : 'visible' }}
                onClick={clearCurrentSelected}
                aria-label='Clear Selection'
                type='button'
              />
            </div>
          </div>
          <div hidden={!!selectedId} className={'mb-2'}>
            <div className={'search-input-container'}>
              <i className='fas fa-search fa-lg text-light-gray'></i>
              <input
                type='text'
                disabled={disabled || readonly}
                value={selectedName || inputText || ''}
                onChange={onSearchFieldChange}
                placeholder={placeholder}
                className='search-input-component typeahead form-control'
                ref={inputRef}
                name={input_name}
                id={input_id}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                aria-label={input_label}
              />
              {modalIsOpened && (
                <a onClick={closeModal}>
                  <i className='fas fa-x fa-lg text-light-gray'></i>
                </a>
              )}
            </div>
          </div>
          <SearchResults
            inputText={inputText}
            searchResults={searchResults}
            adminSiteIds={JSON.parse(global.adminSiteIds)}
            usesAdvancedSiteManagement={JSON.parse(global.usesAdvancedSiteManagement)}
            onClick={onPersonSelected}
            onNewClientSelected={hide_add_new_item_button ? undefined : onNewClientSelected}
            suggestions={!selectedId && suggestions}
            loading={loading}
            showRecentResults={
              !selectedId && (modalIsOpened || !floating_search) && (suggestion_only_on_focus ? isFocused : true)
            }
          />
          {(!suggestions || suggestions.results.length === 0) && showPlaceholder && (
            <div className={'mt-8 mb-8 d-flex flex-column align-items-center'}>
              <i className='fas fa-search fa-2x mb-5'></i>
              <span>{placeholder}</span>
            </div>
          )}
        </div>
      </SearchModal>
      {modalIsOpened && <div className={'mb-14'}></div>}

      <input type={'hidden'} name={hidden_input_name} value={selectedId || ''} ref={valueRef} />
      <input type={'hidden'} name={input_name} ref={inputHiddenRef} />

      {error_message && <div className={'invalid-feedback d-block ms-8'}>{error_message}</div>}

      {anonymous_link && !(disabled || readonly) && !selectedId && (
        <a href={anonymous_link} data-turbo-stream={true} className='ui--link link--primary'>
          Anonymous person
        </a>
      )}
    </Fragment>
  )
}

export default Typeahead
